import { cn } from '@plato/ui/util/classes';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { InputCurrency } from '../ui/input-number.tsx';
import { RadioGroup, RadioGroupItem } from '../ui/radio-group.tsx';
import Text from '../ui/text.tsx';
import type { PriceRangeFilterOption } from './types/filter-types.ts';

type PriceComparisonType = 'greater' | 'smaller';

type PriceRangeFilterProps = {
    filter: PriceRangeFilterOption;
    onChange: (value: { min?: number; max?: number } | null) => void;
    className?: string;
    defaultCurrency: string;
};

export function PriceRangeFilter({ filter, onChange, className, defaultCurrency }: PriceRangeFilterProps) {
    const { t } = useTranslation('filters');
    const [comparisonType, setComparisonType] = useState<PriceComparisonType | undefined>(() => {
        if (filter.selectedRange?.min && !filter.selectedRange.max) {
            return 'greater';
        }
        if (filter.selectedRange?.max && !filter.selectedRange.min) {
            return 'smaller';
        }
        return 'greater';
    });

    const [priceValue, setPriceValue] = useState<string>(() => {
        if (filter.selectedRange?.min) {
            return filter.selectedRange.min.toString();
        }
        if (filter.selectedRange?.max) {
            return filter.selectedRange.max.toString();
        }
        return '0';
    });

    const handleComparisonChange = (type: PriceComparisonType | undefined) => {
        setComparisonType(type);

        if (!type) {
            onChange(null);
            setPriceValue('');
            return;
        }

        const numericValue = priceValue === '' ? undefined : Number(priceValue);
        if (numericValue !== undefined && !Number.isNaN(numericValue)) {
            onChange({
                min: type === 'greater' ? numericValue : undefined,
                max: type === 'smaller' ? numericValue : undefined,
            });
        }
    };
    const handlePriceChange = (value: string) => {
        setPriceValue(value);
        const numericValue = value === '' ? undefined : Number(value);

        if (value === '' || (numericValue !== undefined && !Number.isNaN(numericValue))) {
            if (comparisonType && numericValue !== undefined) {
                onChange({
                    min: comparisonType === 'greater' ? numericValue : undefined,
                    max: comparisonType === 'smaller' ? numericValue : undefined,
                });
            } else if (value === '') {
                onChange(null);
            }
        }
    };

    const handleRadioChange = (value: string) => {
        const type = value as PriceComparisonType;
        handleComparisonChange(type);
    };

    return (
        <div className={cn('flex flex-col space-y-3', className)}>
            <div className="flex items-center space-x-3">
                <div className="flex flex-col gap-2">
                    <Text className="text-muted-foreground">
                        {filter.label} {t('price_range_filter__is')}
                    </Text>
                    <RadioGroup value={comparisonType} onValueChange={handleRadioChange}>
                        <RadioGroupItem value="greater">{t('price_range_filter__greater')}</RadioGroupItem>
                        <RadioGroupItem value="smaller">{t('price_range_filter__smaller')}</RadioGroupItem>
                    </RadioGroup>
                </div>
                <div className="flex flex-col gap-2">
                    <Text className="text-muted-foreground">{t('price_range_filter__than')}</Text>
                    <InputCurrency
                        // placeholder="0"
                        currency={filter.currency ?? defaultCurrency}
                        value={priceValue}
                        onValueChange={({ value }) => {
                            handlePriceChange(value);
                        }}
                        min={filter.minValue}
                        max={filter.maxValue}
                        className="flex-1"
                        decimalScale={2}
                    />
                </div>
            </div>
        </div>
    );
}
